import React, { useEffect } from 'react'

import { useAuth } from 'hooks/UseAuth'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

interface Props {
  externalIntegrationId: number
  refetch: () => void
  setIsRefreshing: React.Dispatch<React.SetStateAction<boolean>>
  onStatusChange?: (status: string, postsCount?: number) => void
}

export const useBestPerformingPostsChannel = ({
  externalIntegrationId,
  refetch,
  setIsRefreshing,
  onStatusChange,
}: Props) => {
  const auth = useAuth()
  const websocketConnection = useWebsocketConnection()

  useEffect(() => {
    if (!auth || !websocketConnection || !externalIntegrationId) return

    const subscription = websocketConnection.subscriptions.create(
      {
        channel: 'ExternalIntegrations::BestPerformingPostsChannel',
        external_integration_id: externalIntegrationId,
      },
      {
        received(data: { status: string; posts_count: number }) {
          onStatusChange?.(data.status, data.posts_count)

          if (data.status === 'processing') {
            setIsRefreshing(true)
          }

          if (data.status === 'failed') {
            setIsRefreshing(false)
            showToastMessage('There was a problem fetching your best performing posts', 'error')
          }

          if (data.status === 'partial_complete') {
            refetch()
          }

          if (data.status === 'complete') {
            refetch()
            setIsRefreshing(false)
            showToastMessage('Done!')
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [auth, websocketConnection, setIsRefreshing, externalIntegrationId, refetch, onStatusChange])
}
